import { useCallback } from 'react';
import type { CombinationResult, Material, WorkbenchMaterial } from '../types';
import { useGameDataContext, useGameStateContext } from '../app/GameProviders';
import { CONSTANTS } from '../data.js';
import { ASSETS } from '../assetManifest.js';
import { Howl } from 'howler';

export function useCombinations(params: {
  inventory: Material[];
  setInventory: React.Dispatch<React.SetStateAction<Material[]>>;
  setCombinationResult: React.Dispatch<React.SetStateAction<CombinationResult | null>>;
  getNextInstanceId: () => string;
  addToPantry: (materialId: string) => void;
}) {
  const { inventory, setInventory, setCombinationResult, getNextInstanceId, addToPantry } = params;
  const { RECIPES, TRIPLE_RECIPES, MATERIALS } = useGameDataContext();
  const { settings } = useGameStateContext();

  const markRecipeAsDiscovered = useCallback((resultId: string) => {
    setInventory(prev => {
      const idx = prev.findIndex(item => item.id === resultId);
      const newMaterial = MATERIALS[resultId] as Material;
      if (!newMaterial) throw new Error(`useCombinations: MATERIALS entry missing for resultId=${resultId}`);
      
      if (idx === -1) {
        // Recipe result doesn't exist in inventory, add it as discovered
        return [...prev, { ...newMaterial, discovered: true }];
      } else {
        // Recipe result exists, mark as discovered if not already
        const existing = prev[idx];
        if (existing.discovered) return prev;
        const updated = [...prev];
        updated[idx] = { ...existing, discovered: true };
        return updated;
      }
    });
  }, [MATERIALS, setInventory]);

  const playSound = useCallback((soundType: 'pair' | 'triple' | 'fail') => {
    const sfxAssets = (ASSETS as any)?.sfx;
    if (!sfxAssets) throw new Error('useCombinations: ASSETS.sfx missing');

    let soundAsset: any;
    let volumeMultiplier: number;

    switch (soundType) {
      case 'pair':
        soundAsset = sfxAssets.combination_pair;
        volumeMultiplier = CONSTANTS.SOUND_EFFECTS.COMBINATION_PAIR_VOLUME;
        break;
      case 'triple':
        soundAsset = sfxAssets.combination_triple;
        volumeMultiplier = CONSTANTS.SOUND_EFFECTS.COMBINATION_TRIPLE_VOLUME;
        break;
      case 'fail':
        soundAsset = sfxAssets.combination_fail;
        volumeMultiplier = CONSTANTS.SOUND_EFFECTS.COMBINATION_FAIL_VOLUME;
        break;
      default:
        throw new Error(`useCombinations: unknown soundType ${soundType}`);
    }

    if (!soundAsset || !soundAsset.path) throw new Error(`useCombinations: sfx asset for ${soundType} is missing path`);

    const volume = (settings.sfxVolume / 100) * volumeMultiplier;
    
    const sound = new Howl({
      src: [soundAsset.path],
      volume: volume,
      preload: true,
    });

    sound.play();
  }, [settings.sfxVolume]);

  const attemptCombine = useCallback((materials: WorkbenchMaterial[], draggedInstanceId: string): WorkbenchMaterial[] => {
    const draggedItem = materials.find(i => i.instanceId === draggedInstanceId);
    
    // If the dragged item is not found, return the materials unchanged
    // This can happen if the item was already removed by another operation
    if (!draggedItem) {
      return materials;
    }

    const neighbors = materials.filter(i => i.instanceId !== draggedInstanceId).filter(other => {
      const d = Math.hypot(draggedItem.x - other.x, draggedItem.y - other.y);
      return d < CONSTANTS.COMBINATION_DISTANCE;
    });

    // Try triple combination first
    if (neighbors.length >= 2) {
      for (let i = 0; i < neighbors.length; i++) {
        for (let j = i + 1; j < neighbors.length; j++) {
          const a = draggedItem.id;
          const b = neighbors[i].id;
          const c = neighbors[j].id;
          const tripleKey = [a, b, c].sort().join('+');
          const tripleResult = TRIPLE_RECIPES[tripleKey];
          if (tripleResult) {
            const newMaterial = MATERIALS[tripleResult] as Material;
            if (!newMaterial) throw new Error(`useCombinations: MATERIALS entry missing for tripleResult=${tripleResult}`);

            const wasAlreadyDiscovered = !!inventory.find(item => item.id === tripleResult && item.discovered);

            // Play triple combination sound
            playSound('triple');

            // Mark recipe as discovered and add to inventory for cookbook
            markRecipeAsDiscovered(tripleResult);

            // Add to pantry for serving loop
            addToPantry(tripleResult);

            // Only show feedback for new discoveries
            if (!wasAlreadyDiscovered) {
              setCombinationResult({
                success: true,
                result: tripleResult,
                message: `New discovery: ${newMaterial.name}!`
              });
              setTimeout(() => setCombinationResult(null), 3000);
            }

            // Replace the three items with the result at average location (snapped)
            const remaining = materials.filter(m =>
              m.instanceId !== draggedItem.instanceId &&
              m.instanceId !== neighbors[i].instanceId &&
              m.instanceId !== neighbors[j].instanceId
            );

            const avgX = Math.round((draggedItem.x + neighbors[i].x + neighbors[j].x) / 3);
            const avgY = Math.round((draggedItem.y + neighbors[i].y + neighbors[j].y) / 3);

            remaining.push({
              id: tripleResult,
              instanceId: getNextInstanceId(),
              x: avgX,
              y: avgY,
            });

            return remaining;
          }
        }
      }
    }

    // Try pair combination
    for (const otherItem of materials) {
      if (otherItem.instanceId === draggedInstanceId) continue;

      const dist = Math.hypot(draggedItem.x - otherItem.x, draggedItem.y - otherItem.y);
      if (dist < CONSTANTS.COMBINATION_DISTANCE) {
        const [id1, id2] = [draggedItem.id, otherItem.id].sort();
        const recipeKey = `${id1}+${id2}`;
        const resultId = RECIPES[recipeKey];

        if (resultId) {
          const newMaterial = MATERIALS[resultId] as Material;
          if (!newMaterial) throw new Error(`useCombinations: MATERIALS entry missing for resultId=${resultId}`);

          const wasAlreadyDiscovered = !!inventory.find(item => item.id === resultId && item.discovered);

          // Play pair combination sound
          playSound('pair');

          // Mark recipe as discovered and add to inventory for cookbook
          markRecipeAsDiscovered(resultId);

          // Add to pantry for serving loop
          addToPantry(resultId);

          // Only show feedback for new discoveries
          if (!wasAlreadyDiscovered) {
            setCombinationResult({
              success: true,
              result: resultId,
              message: `New discovery: ${newMaterial.name}!`
            });
            setTimeout(() => setCombinationResult(null), 3000);
          }

          // Replace the two items with the result at midpoint (snapped)
          const finalMaterials = materials
            .filter(item => item.instanceId !== draggedItem.instanceId && item.instanceId !== otherItem.instanceId);

          const midX = Math.round((draggedItem.x + otherItem.x) / 2);
          const midY = Math.round((draggedItem.y + otherItem.y) / 2);

          finalMaterials.push({
            id: resultId,
            instanceId: getNextInstanceId(),
            x: midX,
            y: midY,
          });

          return finalMaterials;
        } else {
          // Unknown combination
          playSound('fail');

          setCombinationResult({ success: false, message: "These materials don't combine. Try something else!" });
          setTimeout(() => setCombinationResult(null), 2000);
          return materials;
        }
      }
    }

    return materials;
  }, [getNextInstanceId, inventory, setCombinationResult, markRecipeAsDiscovered, addToPantry, playSound, RECIPES, TRIPLE_RECIPES, MATERIALS]);

  return { attemptCombine };
}
